/**
 * System-wide booking oversight.
 *
 * Replaces `renderAdminAppointments`, `openAdminBookingModal`,
 * `createAdminBookingModal`, `updateAdminBookingClinics`,
 * `updateAdminBookingSlots`, `updateAdminBookingPrice`, `handleAdminBooking`,
 * `showAdminBookingSuccessPopup`, `toggleAdminDiscount` and `setAdminDiscount`
 * from admin_dashboard.js.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
  DateInput,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { useBookingActions } from '../../hooks/useBookings';
import { bookingApi } from '../../api/endpoints';
import {
  BOOKING_STATUS,
  compareBookingsChronologically,
  statusLabel,
  statusStyle,
  typeLabel,
} from '../../domain/bookings';
import { statusBreakdown } from '../../domain/stats';
import { calculateBookingPrice, formatCurrency, sumCompletedRevenue } from '../../domain/pricing';
import { arabicDayOf, formatTime12, isoToDisplayDate, todayKey } from '../../domain/dates';

/**
 * Round numbers a receptionist actually gives. A free-text box is still there
 * for the rest — these just remove the typing from the common cases.
 */
const DISCOUNT_PRESETS = [0, 10, 15, 20, 25, 50, 100];

const STATUS_OPTIONS = [
  { value: '', label: 'كل الحالات' },
  { value: BOOKING_STATUS.PENDING, label: 'قيد الانتظار' },
  { value: BOOKING_STATUS.CONFIRMED, label: 'مؤكد' },
  { value: BOOKING_STATUS.COMPLETED, label: 'مكتمل' },
  { value: BOOKING_STATUS.CANCELLED, label: 'ملغي' },
];

export function AdminAppointments() {
  const { doctors } = useData();
  const { success, error: toastError } = useNotifications();

  const [dateKey, setDateKey] = useState(todayKey());
  const [doctorId, setDoctorId] = useState('');
  const [status, setStatus] = useState('');
  const [discountTarget, setDiscountTarget] = useState(null);

  const filters = useMemo(
    () => ({
      ...(dateKey ? { date: dateKey } : {}),
      ...(doctorId ? { doctorId } : {}),
      ...(status ? { status } : {}),
    }),
    [dateKey, doctorId, status],
  );

  const state = useAsync(() => bookingApi.list(filters), [dateKey, doctorId, status], {
    initialData: [],
  });

  const bookings = useMemo(
    () => [...(state.data || [])].sort(compareBookingsChronologically),
    [state.data],
  );

  const actions = useBookingActions({ bookings, setBookings: state.setData });

  const totals = useMemo(() => {
    const counts = statusBreakdown(bookings);
    return {
      total: bookings.length,
      confirmed: counts[BOOKING_STATUS.CONFIRMED] || 0,
      pending: counts[BOOKING_STATUS.PENDING] || 0,
      revenue: sumCompletedRevenue(bookings),
    };
  }, [bookings]);

  const doctorName = (id) => doctors.find((d) => String(d.id) === String(id))?.name;

  const run = async (fn, message) => {
    try {
      await fn();
      success(message);
    } catch (err) {
      toastError(err.message || 'تعذر تنفيذ العملية');
    }
  };

  /**
   * Apply a discount to one booking.
   *
   * Only the percentage is sent. The backend recomputes the total from the
   * figures it already holds, so the two can never disagree — and it replies
   * with the stored row, which is what goes back into the list rather than a
   * locally guessed price.
   */
  const applyDiscount = async (percent) => {
    if (!discountTarget) return;
    try {
      const result = await bookingApi.setAdminDiscount(discountTarget.id, percent);
      state.setData((current) =>
        (current || []).map((entry) =>
          String(entry.id) === String(discountTarget.id) ? { ...entry, ...result.booking } : entry,
        ),
      );
      success(percent > 0 ? `تم تطبيق خصم ${percent}%` : 'تم إلغاء الخصم');
      setDiscountTarget(null);
    } catch (err) {
      toastError(err.message || 'تعذر تطبيق الخصم');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="الحجوزات"
        subtitle={dateKey ? `${arabicDayOf(dateKey)} — ${isoToDisplayDate(dateKey)}` : 'كل التواريخ'}
        icon="calendar-check"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            تحديث
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="calendar-check-2" label="إجمالي الحجوزات" value={totals.total} accent="cyan" />
        <StatCard icon="check-circle-2" label="مؤكدة" value={totals.confirmed} accent="emerald" />
        <StatCard icon="clock" label="قيد الانتظار" value={totals.pending} accent="amber" />
        <StatCard
          icon="banknote"
          label="إيراد المكتمل"
          value={formatCurrency(totals.revenue)}
          accent="purple"
        />
      </div>

      <Panel>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <DateInput
            value={dateKey}
            onChange={(event) => setDateKey(event.target.value)}
          />

          <Select value={doctorId} onChange={(event) => setDoctorId(event.target.value)}>
            <option value="">كل الأطباء</option>
            {doctors.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>

          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {dateKey && (
          <button
            type="button"
            onClick={() => setDateKey('')}
            className="mt-3 text-xs font-bold text-cyan-400 hover:text-cyan-300"
          >
            عرض كل التواريخ
          </button>
        )}
      </Panel>

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && bookings.length === 0 ? (
        <Spinner label="جاري تحميل الحجوزات..." />
      ) : bookings.length === 0 ? (
        <Panel>
          <EmptyState
            icon="calendar-x"
            title="لا توجد حجوزات"
            message="جرّب تغيير التاريخ أو إزالة عوامل التصفية."
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking) => {
            const busy = String(actions.pendingId) === String(booking.id);
            // Same signal the doctor sees on their own day grid: a discounted
            // visit is charged something other than the clinic price list, and
            // it is marked in white on both screens so they describe it alike.
            const discounted = Number(booking.adminDiscount) > 0;
            return (
              <Panel
                key={booking.id}
                className={`flex flex-wrap items-center gap-4 ${
                  discounted ? 'border border-white/60 bg-white/[0.07]' : ''
                }`}
              >
                <span className="shrink-0 rounded-lg bg-slate-800 px-2.5 py-1.5 font-mono text-xs text-cyan-300">
                  {formatTime12(booking.time)}
                </span>

                <Avatar name={booking.patientName} size={40} />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-bold text-white">{booking.patientName}</h3>
                    <Badge className={statusStyle(booking.status)}>
                      {statusLabel(booking.status)}
                    </Badge>
                    {booking.depositRequired && !booking.depositConfirmed && (
                      <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="wallet">
                        عربون معلق
                      </Badge>
                    )}
                    {discounted && (
                      <Badge className="border-white/70 bg-white text-slate-900" icon="percent">
                        خصم إدارة {booking.adminDiscount}%
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                    <span>
                      {booking.doctorName || doctorName(booking.doctorId) || `طبيب #${booking.doctorId}`}
                    </span>
                    <span>{typeLabel(booking.type)}</span>
                    {!dateKey && <span>{isoToDisplayDate(booking.date)}</span>}
                    <span className="flex items-center gap-1.5 font-bold text-emerald-400">
                      {discounted && Number(booking.originalPrice) > Number(booking.finalPrice) && (
                        <span className="text-slate-500 line-through">
                          {formatCurrency(booking.originalPrice)}
                        </span>
                      )}
                      {formatCurrency(booking.finalPrice)}
                    </span>
                    {(booking.selectedServices || []).length > 0 && (
                      <span className="text-cyan-300">
                        + {booking.selectedServices.length} خدمة
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2">
                  {booking.status !== BOOKING_STATUS.CANCELLED && (
                    <Button
                      variant="subtle"
                      icon="percent"
                      onClick={() => setDiscountTarget(booking)}
                    >
                      خصم
                    </Button>
                  )}
                  {booking.status === BOOKING_STATUS.PENDING && (
                    <Button
                      variant="success"
                      icon="check"
                      loading={busy}
                      onClick={() => run(() => actions.confirm(booking.id), 'تم تأكيد الحجز')}
                    >
                      تأكيد
                    </Button>
                  )}
                  {booking.status !== BOOKING_STATUS.CANCELLED &&
                    booking.status !== BOOKING_STATUS.COMPLETED && (
                      <Button
                        variant="danger"
                        icon="x"
                        loading={busy}
                        onClick={() => run(() => actions.cancel(booking.id), 'تم إلغاء الحجز')}
                      >
                        إلغاء
                      </Button>
                    )}
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      <DiscountModal
        booking={discountTarget}
        onClose={() => setDiscountTarget(null)}
        onApply={applyDiscount}
      />
    </div>
  );
}

/* ========================================================================== */

/**
 * Set or clear the admin discount on one booking.
 *
 * Shows the resulting price before it is applied, because a percentage is not
 * what anyone is actually deciding — the figure the patient will be asked for
 * is. Extras are listed separately and left out of the discount: a courtesy
 * discount is on the visit fee, not on a lab test someone else performs.
 */
function DiscountModal({ booking, onClose, onApply }) {
  const [percent, setPercent] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPercent(String(Number(booking?.adminDiscount) || 0));
  }, [booking]);

  if (!booking) return null;

  const extrasTotal = (booking.selectedServices || []).reduce(
    (sum, service) => sum + (Number(service.price) || 0),
    0,
  );

  const originalTotal = Number(booking.originalPrice) || 0;
  const consultationBase = Math.max(0, originalTotal - extrasTotal);

  // The same cumulative chain the backend applies, so the preview and the
  // stored result agree rather than merely being close.
  const chain = calculateBookingPrice(consultationBase, {
    day: booking.lockedDayDiscount || 0,
    night: booking.lockedNightDiscount || 0,
    admin: Number(percent) || 0,
    voucher: booking.voucherDiscount || 0,
  });
  const preview = chain.finalPrice + extrasTotal;

  const handleApply = async () => {
    setSaving(true);
    try {
      await onApply(Math.min(100, Math.max(0, Number(percent) || 0)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="خصم إداري"
      icon="percent"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="check" loading={saving} onClick={handleApply}>
            تطبيق
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-white/5 bg-white/5 p-3">
          <p className="text-sm font-bold text-white">{booking.patientName}</p>
          <p className="mt-0.5 text-xs text-slate-400">
            {booking.doctorName} — {isoToDisplayDate(booking.date)} —{' '}
            {formatTime12(booking.time)}
          </p>
        </div>

        <Field label="نسبة الخصم على الكشف (%)">
          <Input
            type="number"
            min="0"
            max="100"
            value={percent}
            onChange={(event) => setPercent(event.target.value)}
          />
        </Field>

        <div className="flex flex-wrap gap-1.5">
          {DISCOUNT_PRESETS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setPercent(String(value))}
              className={`rounded-lg border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                Number(percent) === value
                  ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                  : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
              }`}
            >
              {value === 0 ? 'بدون خصم' : `${value}%`}
            </button>
          ))}
        </div>

        <dl className="space-y-1.5 rounded-xl border border-white/5 bg-slate-900/60 p-3 text-sm">
          <Row label="سعر الكشف" value={formatCurrency(consultationBase)} />
          {extrasTotal > 0 && (
            <Row
              label={`خدمات إضافية (${booking.selectedServices.length})`}
              value={`+ ${formatCurrency(extrasTotal)}`}
            />
          )}
          <div className="flex items-center justify-between border-t border-white/10 pt-2">
            <dt className="text-xs font-bold text-white">الإجمالي بعد الخصم</dt>
            <dd className="text-base font-bold text-emerald-400">{formatCurrency(preview)}</dd>
          </div>
        </dl>

        <p className="flex items-start gap-2 rounded-lg border border-white/20 bg-white/10 p-2.5 text-[11px] leading-relaxed text-slate-300">
          <Icon name="info" size={13} className="mt-0.5 shrink-0" />
          الحجز المخصوم يظهر باللون الأبيض في تقويم الطبيب. العربون المحوَّل مسبقاً لا يُعاد
          حسابه — يُسوّى الفرق في العيادة.
        </p>
      </div>
    </Modal>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="truncate text-xs font-bold text-slate-200">{value}</dd>
    </div>
  );
}

export default AdminAppointments;
